const POINTER_QUERY = '(hover: hover) and (pointer: fine)';
const LINK_CURSOR_SELECTOR = ['a', '[role="link"]'].join(',');
const NATIVE_CURSOR_SELECTOR = ['input', 'textarea', 'select', '[contenteditable="true"]', '[contenteditable=""]'].join(',');

class CustomCursor {
    constructor() {
        this.root = document.documentElement;
        this.cursor = document.createElement('div');
        this.raf = null;
        this.mouseX = window.innerWidth / 2;
        this.mouseY = window.innerHeight / 2;
        this.cursorX = this.mouseX;
        this.cursorY = this.mouseY;
        this.cursorSpeed = 0.2;
        this.scaleCurrent = 1;
        this.scaleTarget = 1;
        this.scaleEase = 0.2;
        this.isCustomCursorTarget = false;

        this.cursor.className = 'custom-cursor';
        this.cursor.setAttribute('aria-hidden', 'true');
        document.body.appendChild(this.cursor);
        this.root.classList.add('custom-cursor--enabled');

        this.handlePointerMove = this.handlePointerMove.bind(this);
        this.handlePointerDown = this.handlePointerDown.bind(this);
        this.handlePointerUp = this.handlePointerUp.bind(this);
        this.handlePointerLeave = this.handlePointerLeave.bind(this);
        this.handleWindowBlur = this.handleWindowBlur.bind(this);

        window.addEventListener('pointermove', this.handlePointerMove, { passive: true });
        window.addEventListener('pointerdown', this.handlePointerDown, { passive: true });
        window.addEventListener('pointerup', this.handlePointerUp, { passive: true });
        document.documentElement.addEventListener('pointerleave', this.handlePointerLeave, { passive: true });
        window.addEventListener('blur', this.handleWindowBlur);

        this.animate();
    }

    handlePointerMove(event) {
        if (event.pointerType && event.pointerType !== 'mouse') return;

        this.mouseX = event.clientX;
        this.mouseY = event.clientY;
        this.updateTargetState(event.target);
        this.root.classList.toggle('custom-cursor--is-visible', this.isCustomCursorTarget);
    }

    handlePointerDown(event) {
        if (event.pointerType && event.pointerType !== 'mouse') return;

        if (!this.isCustomCursorTarget) return;

        this.root.classList.add('custom-cursor--is-pressed');
        this.scaleTarget = 0.8;
    }

    handlePointerUp(event) {
        if (event.pointerType && event.pointerType !== 'mouse') return;

        this.root.classList.remove('custom-cursor--is-pressed');
        this.scaleTarget = 1;
    }

    handlePointerLeave() {
        this.hide();
    }

    handleWindowBlur() {
        this.hide();
    }

    updateTargetState(target) {
        if (!(target instanceof Element)) return;

        const isNativeCursorTarget = Boolean(target.closest(NATIVE_CURSOR_SELECTOR));
        const isLinkCursorTarget = !isNativeCursorTarget && Boolean(target.closest(LINK_CURSOR_SELECTOR));

        this.isCustomCursorTarget = !isNativeCursorTarget;

        this.root.classList.toggle('custom-cursor--is-link', isLinkCursorTarget);

        if (!this.root.classList.contains('custom-cursor--is-pressed')) {
            this.scaleTarget = 1;
        }
    }

    hide() {
        this.root.classList.remove(
            'custom-cursor--is-visible',
            'custom-cursor--is-pressed',
            'custom-cursor--is-link'
        );
        this.isCustomCursorTarget = false;
        this.scaleTarget = 1;
    }

    animate() {
        this.cursorX += (this.mouseX - this.cursorX) * this.cursorSpeed;
        this.cursorY += (this.mouseY - this.cursorY) * this.cursorSpeed;
        this.scaleCurrent += (this.scaleTarget - this.scaleCurrent) * this.scaleEase;

        this.cursor.style.transform = `translate3d(${this.cursorX}px, ${this.cursorY}px, 0) translate(var(--custom-cursor-offset-x), var(--custom-cursor-offset-y)) rotate(var(--custom-cursor-rotation)) scale(${this.scaleCurrent})`;

        this.raf = requestAnimationFrame(() => this.animate());
    }
}

function shouldUseCustomCursor() {
    return (
        window.matchMedia &&
        window.matchMedia(POINTER_QUERY).matches &&
        window.name !== 'neos-content-main' &&
        !document.documentElement.classList.contains('neos-backend') &&
        !document.body.classList.contains('neos-backend') &&
        !document.querySelector('.custom-cursor')
    );
}

function initCustomCursor() {
    if (!shouldUseCustomCursor()) return;

    new CustomCursor();
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initCustomCursor);
} else {
    initCustomCursor();
}
